import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createTour, updateTour, getAdminTours, getAdminCategories, getLevels } from '../../services/api';
import { ArrowLeft, Plus, Trash2, Loader2, Check, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import ImageUploader from './ImageUploader';

const emptyTour = {
  title: '', slug: '', category: '', location: '', region: 'Türkiye', duration: '',
  nights: 0, groupSize: 15, minAge: 12, maxAge: 70, level: 3, levelName: 'Orta',
  rating: 0, reviewCount: 0, originalPrice: null, salePrice: 0, currency: '₺',
  onSale: false, active: true, heroImage: '', heroImageAlt: '', images: [],
  shortDescription: '', description: '', highlights: [], included: [], notIncluded: [],
  itinerary: [], pricingDates: [], guide: { name: '', image: '', bio: '', instagram: '' },
  reviews: [], categoryIds: [],
  // SEO / AEO
  metaTitle: '', metaDescription: '', focusKeyphrase: '', faqItems: [],
};

const AdminTourForm = () => {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!tourId;
  const [form, setForm] = useState(emptyTour);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('basic');
  const [allCategories, setAllCategories] = useState([]);
  const [allLevels, setAllLevels] = useState([]);

  // Temp fields for adding items
  const [tempHighlight, setTempHighlight] = useState('');
  const [tempIncluded, setTempIncluded] = useState('');
  const [tempNotIncluded, setTempNotIncluded] = useState('');
  const [tempImage, setTempImage] = useState('');

  useEffect(() => {
    getAdminCategories().then(res => setAllCategories(res.data || [])).catch(() => {});
    getLevels().then(res => setAllLevels(res.data || [])).catch(() => {});
    if (isEdit) {
      getAdminTours().then(res => {
        const tour = res.data.find(t => t.id === tourId);
        if (tour) setForm({ ...emptyTour, ...tour, categoryIds: tour.categoryIds || [] });
        setLoading(false);
      });
    }
  }, [tourId, isEdit]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const setGuide = (key, val) => setForm(prev => ({ ...prev, guide: { ...prev.guide, [key]: val } }));

  const addToList = (key, value, setter) => {
    if (!value.trim()) return;
    set(key, [...(form[key] || []), value.trim()]);
    setter('');
  };

  const removeFromList = (key, idx) => {
    set(key, form[key].filter((_, i) => i !== idx));
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ş/g, 's')
      .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
      .replace(/Ü/g, 'u').replace(/Ö/g, 'o').replace(/Ş/g, 's')
      .replace(/Ç/g, 'c').replace(/Ğ/g, 'g').replace(/İ/g, 'i')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...form };
      if (!data.slug) data.slug = generateSlug(data.title);
      data.nights = Number(data.nights) || 0;
      data.groupSize = Number(data.groupSize) || 15;
      data.level = Number(data.level) || 1;
      data.salePrice = Number(data.salePrice) || 0;
      data.originalPrice = data.originalPrice ? Number(data.originalPrice) : null;

      if (isEdit) {
        await updateTour(tourId, data);
      } else {
        await createTour(data);
      }
      navigate('/admin/turlar');
    } catch (err) {
      alert(err.response?.data?.detail || 'Hata oluştu');
    } finally { setSaving(false); }
  };

  // Pricing date management
  const addPricingDate = () => {
    set('pricingDates', [...(form.pricingDates || []), { date: '', perPerson: 0, singleRoom: 0, extraBed: 0, baby: 0, child: 0, available: true }]);
  };
  const updatePricingDate = (idx, key, val) => {
    const updated = [...form.pricingDates];
    updated[idx] = { ...updated[idx], [key]: val };
    set('pricingDates', updated);
  };
  const removePricingDate = (idx) => {
    set('pricingDates', form.pricingDates.filter((_, i) => i !== idx));
  };
  const movePricingDate = (from, to) => {
    if (to < 0 || to >= (form.pricingDates || []).length) return;
    const next = [...form.pricingDates];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    set('pricingDates', next);
  };
  // Sort by parsed leading date in the free-form "date" field (fallback: keep order)
  // Handles Turkish months, e.g. "18 - 22 Mart 2025" or "5 Nisan 2026 - 10 Nisan 2026"
  const TR_MONTHS = { ocak: 0, subat: 1, şubat: 1, mart: 2, nisan: 3, mayis: 4, mayıs: 4, haziran: 5, temmuz: 6, agustos: 7, ağustos: 7, eylul: 8, eylül: 8, ekim: 9, kasim: 10, kasım: 10, aralik: 11, aralık: 11 };
  const parsePricingDate = (s = '') => {
    const norm = String(s).toLowerCase().replace(/[.,]/g, ' ');
    const m = norm.match(/(\d{1,2})\s*[-–]?\s*\d{0,2}?\s*([a-zçğıöşü]+)\s*(\d{4})/);
    if (!m) return Number.MAX_SAFE_INTEGER;
    const day = parseInt(m[1], 10);
    const monthName = m[2].replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u');
    const month = TR_MONTHS[monthName];
    if (month === undefined) return Number.MAX_SAFE_INTEGER;
    const year = parseInt(m[3], 10);
    return new Date(year, month, day).getTime();
  };
  const sortPricingDatesByDate = () => {
    const sorted = [...(form.pricingDates || [])].sort((a, b) => parsePricingDate(a.date) - parsePricingDate(b.date));
    set('pricingDates', sorted);
  };
  const [dragIdx, setDragIdx] = useState(null);
  const onDragStart = (e, idx) => { setDragIdx(idx); try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)); } catch (_) { /* noop */ } };
  const onDragOver = (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'; };
  const onDropAt = (e, idx) => {
    e.preventDefault();
    const from = dragIdx ?? Number(e.dataTransfer?.getData('text/plain') || -1);
    setDragIdx(null);
    if (from === -1 || from === idx) return;
    movePricingDate(from, idx);
  };

  // Itinerary management
  const addDay = () => {
    const nextDay = (form.itinerary || []).length + 1;
    set('itinerary', [...(form.itinerary || []), { day: nextDay, title: '', description: '', distance: '', elevation: '', difficulty: '', meals: [], accommodation: '' }]);
  };
  const updateDay = (idx, key, val) => {
    const updated = [...form.itinerary];
    updated[idx] = { ...updated[idx], [key]: val };
    set('itinerary', updated);
  };
  const removeDay = (idx) => set('itinerary', form.itinerary.filter((_, i) => i !== idx));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#3D4A26]" size={32} /></div>;

  const tabs = [
    { key: 'basic', label: 'Temel Bilgiler' },
    { key: 'content', label: 'İçerik' },
    { key: 'itinerary', label: 'Program' },
    { key: 'pricing', label: 'Tarihler & Fiyatlar' },
    { key: 'guide', label: 'Rehber' },
    { key: 'seo', label: 'SEO / AEO' },
    { key: 'faq', label: `SSS (${(form.faqItems || []).length})` },
  ];

  // FAQ helpers
  const addFaq = () => set('faqItems', [...(form.faqItems || []), { question: '', answer: '' }]);
  const updateFaq = (idx, field, value) => {
    const next = [...(form.faqItems || [])];
    next[idx] = { ...next[idx], [field]: value };
    set('faqItems', next);
  };
  const removeFaq = (idx) => set('faqItems', (form.faqItems || []).filter((_, i) => i !== idx));

  return (
    <div>
      <button onClick={() => navigate('/admin/turlar')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#1B1B1B] mb-4 transition-colors">
        <ArrowLeft size={16} /> Turlara Dön
      </button>

      <form onSubmit={handleSubmit}>
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {tabs.map(t => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                tab === t.key ? 'bg-[#3D4A26] text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >{t.label}</button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          {/* Basic Info */}
          {tab === 'basic' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold mb-1">Tur Adı *</label>
                  <input value={form.title} onChange={e => { set('title', e.target.value); if (!isEdit) set('slug', generateSlug(e.target.value)); }}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" required />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Slug</label>
                  <input value={form.slug} onChange={e => set('slug', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] text-gray-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><label className="block text-xs font-semibold mb-1">Konum</label><input value={form.location} onChange={e => set('location', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
                <div><label className="block text-xs font-semibold mb-1">Bölge</label><input value={form.region} onChange={e => set('region', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
                <div><label className="block text-xs font-semibold mb-1">Süre</label><input value={form.duration} onChange={e => set('duration', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" placeholder="4 Gece 5 Gün" /></div>
              </div>

              {/* Multi-Category Selector */}
              <div>
                <label className="block text-xs font-semibold mb-2">Kategoriler (birden fazla seçilebilir)</label>
                {allCategories.length === 0 ? (
                  <p className="text-xs text-gray-400">Önce admin → Kategoriler sayfasından kategori ekleyin.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {['route', 'collection'].map(typeKey => {
                      const list = allCategories.filter(c => c.type === typeKey);
                      if (list.length === 0) return null;
                      const label = typeKey === 'route' ? 'Rotalar' : 'Koleksiyonlar';
                      return (
                        <div key={typeKey} className="border border-gray-200 rounded-lg p-3">
                          <div className="text-[11px] uppercase tracking-wider font-bold text-[#3D4A26] mb-2">{label}</div>
                          <div className="space-y-1.5">
                            {list.map(cat => {
                              const checked = (form.categoryIds || []).includes(cat.id);
                              return (
                                <label key={cat.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 transition-colors" data-testid={`tour-category-checkbox-${cat.id}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      const cur = form.categoryIds || [];
                                      const next = checked ? cur.filter(id => id !== cat.id) : [...cur, cat.id];
                                      set('categoryIds', next);
                                    }}
                                    className="w-4 h-4 accent-[#C8D843]"
                                  />
                                  <span className="text-sm text-[#1B1B1B]">{cat.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div><label className="block text-xs font-semibold mb-1">Gece</label><input type="number" value={form.nights} onChange={e => set('nights', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
                <div><label className="block text-xs font-semibold mb-1">Grup Max</label><input type="number" value={form.groupSize} onChange={e => set('groupSize', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Seviye</label>
                  <select
                    value={form.level || 1}
                    onChange={e => {
                      const lvl = Number(e.target.value);
                      const match = allLevels.find(l => l.level === lvl);
                      set('level', lvl);
                      if (match) set('levelName', match.name);
                    }}
                    data-testid="tour-level-select"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] bg-white"
                  >
                    {(allLevels.length > 0 ? allLevels : [{level:1,name:'Kolay'}]).map(l => (
                      <option key={l.level} value={l.level}>
                        Seviye {l.level} · {l.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div><label className="block text-xs font-semibold mb-1">&nbsp;</label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.onSale} onChange={e => set('onSale', e.target.checked)} className="w-4 h-4 accent-[#C8D843]" />
                    <span className="text-sm font-medium">İndirimli</span>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><label className="block text-xs font-semibold mb-1">Fiyat (₺)</label><input type="number" value={form.salePrice} onChange={e => set('salePrice', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
                <div><label className="block text-xs font-semibold mb-1">Eski Fiyat</label><input type="number" value={form.originalPrice || ''} onChange={e => set('originalPrice', e.target.value || null)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
                <div><label className="block text-xs font-semibold mb-1">Hero Görsel URL</label><input value={form.heroImage} onChange={e => set('heroImage', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
              </div>
              {/* Images + Cover Image Selector */}
              <div>
                <label className="block text-xs font-semibold mb-2">Görseller</label>

                {/* File upload (primary) */}
                <div className="mb-3">
                  <ImageUploader
                    category="tours"
                    multiple
                    label="Görsel Yükle"
                    onUpload={(url) => set('images', [...(form.images || []), url])}
                  />
                </div>

                {/* URL input (secondary) */}
                <div className="flex gap-2 mb-2">
                  <input value={tempImage} onChange={e => setTempImage(e.target.value)} placeholder="veya URL yapıştır" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                  <button type="button" data-testid="add-image-button" onClick={() => addToList('images', tempImage, setTempImage)} className="px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"><Plus size={16} /></button>
                </div>
                {(form.images || []).length > 0 && (
                  <p className="text-[11px] text-gray-500 mb-2">Kapak fotoğrafı olarak kullanmak istediğiniz görseli seçin (yeşil çerçeve = kapak)</p>
                )}
                <div className="flex gap-2 flex-wrap">
                  {(form.images || []).map((img, i) => {
                    const isCover = form.heroImage === img;
                    return (
                      <div key={i} className="relative group">
                        <button
                          type="button"
                          onClick={() => set('heroImage', img)}
                          data-testid={`cover-image-select-${i}`}
                          title={isCover ? 'Kapak fotoğrafı' : 'Kapak yap'}
                          className={`block rounded-lg overflow-hidden border-2 transition-all ${isCover ? 'border-[#C8D843] ring-2 ring-[#C8D843]/40' : 'border-transparent hover:border-gray-300'}`}
                        >
                          <img src={img} alt="" className="w-20 h-20 object-cover" />
                        </button>
                        {isCover && (
                          <span className="absolute top-1 left-1 bg-[#C8D843] text-[#1B1B1B] text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                            <Check size={8} strokeWidth={3} /> KAPAK
                          </span>
                        )}
                        <button type="button" onClick={() => {
                          if (form.heroImage === img) set('heroImage', '');
                          removeFromList('images', i);
                        }} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={10} /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          {tab === 'content' && (
            <div className="space-y-4">
              <div><label className="block text-xs font-semibold mb-1">Kısa Açıklama</label><textarea value={form.shortDescription} onChange={e => set('shortDescription', e.target.value)} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] resize-none" /></div>
              <div><label className="block text-xs font-semibold mb-1">Detaylı Açıklama</label><textarea value={form.description} onChange={e => set('description', e.target.value)} rows={6} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] resize-none" /></div>
              {/* Highlights */}
              <div>
                <label className="block text-xs font-semibold mb-1">Öne Çıkan Özellikler</label>
                <div className="flex gap-2 mb-2">
                  <input value={tempHighlight} onChange={e => setTempHighlight(e.target.value)} placeholder="Özellik ekle" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addToList('highlights', tempHighlight, setTempHighlight); }}} />
                  <button type="button" onClick={() => addToList('highlights', tempHighlight, setTempHighlight)} className="px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"><Plus size={16} /></button>
                </div>
                <ul className="space-y-1">{(form.highlights || []).map((h, i) => (<li key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm"><span>{h}</span><button type="button" onClick={() => removeFromList('highlights', i)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button></li>))}</ul>
              </div>
              {/* Included */}
              <div>
                <label className="block text-xs font-semibold mb-1">Dahil Olan Hizmetler</label>
                <div className="flex gap-2 mb-2">
                  <input value={tempIncluded} onChange={e => setTempIncluded(e.target.value)} placeholder="Hizmet ekle" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addToList('included', tempIncluded, setTempIncluded); }}} />
                  <button type="button" onClick={() => addToList('included', tempIncluded, setTempIncluded)} className="px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"><Plus size={16} /></button>
                </div>
                <ul className="space-y-1">{(form.included || []).map((h, i) => (<li key={i} className="flex items-center justify-between bg-green-50 rounded-lg px-3 py-2 text-sm"><span>{h}</span><button type="button" onClick={() => removeFromList('included', i)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button></li>))}</ul>
              </div>
              {/* Not Included */}
              <div>
                <label className="block text-xs font-semibold mb-1">Dahil Olmayan Hizmetler</label>
                <div className="flex gap-2 mb-2">
                  <input value={tempNotIncluded} onChange={e => setTempNotIncluded(e.target.value)} placeholder="Hizmet ekle" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addToList('notIncluded', tempNotIncluded, setTempNotIncluded); }}} />
                  <button type="button" onClick={() => addToList('notIncluded', tempNotIncluded, setTempNotIncluded)} className="px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"><Plus size={16} /></button>
                </div>
                <ul className="space-y-1">{(form.notIncluded || []).map((h, i) => (<li key={i} className="flex items-center justify-between bg-red-50 rounded-lg px-3 py-2 text-sm"><span>{h}</span><button type="button" onClick={() => removeFromList('notIncluded', i)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button></li>))}</ul>
              </div>
            </div>
          )}

          {/* Itinerary */}
          {tab === 'itinerary' && (
            <div className="space-y-4">
              {(form.itinerary || []).map((day, idx) => (
                <div key={idx} className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-bold text-sm">Gün {day.day}</span>
                    <button type="button" onClick={() => removeDay(idx)} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input placeholder="Başlık" value={day.title || ''} onChange={e => updateDay(idx, 'title', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                    <input placeholder="Mesafe" value={day.distance || ''} onChange={e => updateDay(idx, 'distance', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                  </div>
                  <textarea placeholder="Açıklama" value={day.description || ''} onChange={e => updateDay(idx, 'description', e.target.value)} rows={2} className="w-full mt-2 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] resize-none" />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                    <input placeholder="İrtifa" value={day.elevation || ''} onChange={e => updateDay(idx, 'elevation', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                    <input placeholder="Zorluk" value={day.difficulty || ''} onChange={e => updateDay(idx, 'difficulty', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                    <input placeholder="Öğünler (virgülle)" value={(day.meals || []).join(', ')} onChange={e => updateDay(idx, 'meals', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                    <input placeholder="Konaklama" value={day.accommodation || ''} onChange={e => updateDay(idx, 'accommodation', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addDay} className="flex items-center gap-2 text-sm font-medium text-[#3D4A26] hover:text-[#2d3a1a] transition-colors"><Plus size={16} /> Gün Ekle</button>
            </div>
          )}

          {/* Pricing Dates */}
          {tab === 'pricing' && (
            <div className="space-y-4">
              {(form.pricingDates || []).length > 1 && (
                <div className="flex items-center justify-between bg-[#F5F5F0] rounded-lg px-4 py-2.5">
                  <p className="text-xs text-gray-600">Sürükle-bırak ile ya da yukarı/aşağı oklarla sıralayın. Public site bu sıralamayı gösterir.</p>
                  <button
                    type="button"
                    onClick={sortPricingDatesByDate}
                    data-testid="pricing-sort-by-date-btn"
                    className="text-xs font-semibold text-[#3D4A26] hover:text-[#2d3a1a] whitespace-nowrap ml-3"
                  >
                    Tarihe göre sırala ↓
                  </button>
                </div>
              )}
              {(form.pricingDates || []).map((pd, idx) => (
                <div
                  key={idx}
                  draggable
                  onDragStart={(e) => onDragStart(e, idx)}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDropAt(e, idx)}
                  data-testid={`pricing-date-card-${idx}`}
                  className={`border border-gray-200 rounded-xl p-4 bg-white transition-all ${dragIdx === idx ? 'opacity-40' : ''} ${dragIdx !== null && dragIdx !== idx ? 'hover:border-[#3D4A26]' : ''}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="cursor-move text-gray-300 hover:text-gray-500 select-none"
                        title="Sürükle"
                        data-testid={`pricing-drag-handle-${idx}`}
                      >
                        <GripVertical size={18} />
                      </span>
                      <span className="font-bold text-sm">Tarih {idx + 1}</span>
                      {(pd.capacity > 0 || pd.booked > 0) && (
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-[#3D4A26] bg-[#F5F5F0] px-2 py-0.5 rounded-full">
                          {Math.max(0, ((pd.capacity > 0 ? pd.capacity : form.groupSize) - (pd.booked || 0)))}/{pd.capacity > 0 ? pd.capacity : form.groupSize} kaldı
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => movePricingDate(idx, idx - 1)}
                        disabled={idx === 0}
                        title="Yukarı taşı"
                        data-testid={`pricing-move-up-${idx}`}
                        className="p-1 text-gray-400 hover:text-[#3D4A26] disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => movePricingDate(idx, idx + 1)}
                        disabled={idx === (form.pricingDates || []).length - 1}
                        title="Aşağı taşı"
                        data-testid={`pricing-move-down-${idx}`}
                        className="p-1 text-gray-400 hover:text-[#3D4A26] disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronDown size={16} />
                      </button>
                      <button type="button" onClick={() => removePricingDate(idx)} className="text-red-400 hover:text-red-600 p-1 ml-1">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <input placeholder="Tarih (ör: 18 - 22 Mart 2025)" value={pd.date} onChange={e => updatePricingDate(idx, 'date', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                    <input type="number" placeholder="Kişi Başı" value={pd.perPerson} onChange={e => updatePricingDate(idx, 'perPerson', Number(e.target.value))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                    <input type="number" placeholder="Tek Kişilik" value={pd.singleRoom} onChange={e => updatePricingDate(idx, 'singleRoom', Number(e.target.value))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                    <input type="number" placeholder="İlave Yatak" value={pd.extraBed} onChange={e => updatePricingDate(idx, 'extraBed', Number(e.target.value))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                    <input type="number" placeholder="Bebek" value={pd.baby} onChange={e => updatePricingDate(idx, 'baby', Number(e.target.value))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                    <input type="number" placeholder="Çocuk" value={pd.child} onChange={e => updatePricingDate(idx, 'child', Number(e.target.value))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100">
                    <div>
                      <label className="block text-[10px] text-gray-500 uppercase tracking-widest mb-1">Kapasite</label>
                      <input type="number" min="0" placeholder={`${form.groupSize} (varsayılan)`} value={pd.capacity || ''} onChange={e => updatePricingDate(idx, 'capacity', Number(e.target.value))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" data-testid={`pricing-capacity-${idx}`} />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 uppercase tracking-widest mb-1">Dolu</label>
                      <input type="number" min="0" placeholder="0" value={pd.booked || 0} onChange={e => updatePricingDate(idx, 'booked', Number(e.target.value))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" data-testid={`pricing-booked-${idx}`} />
                    </div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                        <input type="checkbox" checked={pd.available !== false} onChange={e => updatePricingDate(idx, 'available', e.target.checked)} className="w-3.5 h-3.5 accent-[#C8D843]" />
                        Satışa açık
                      </label>
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" onClick={addPricingDate} data-testid="pricing-add-date-btn" className="flex items-center gap-2 text-sm font-medium text-[#3D4A26] hover:text-[#2d3a1a] transition-colors"><Plus size={16} /> Tarih Ekle</button>
            </div>
          )}

          {/* Guide */}
          {tab === 'guide' && (
            <div className="space-y-4 max-w-lg">
              <div><label className="block text-xs font-semibold mb-1">Rehber Adı</label><input value={form.guide?.name || ''} onChange={e => setGuide('name', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
              <div><label className="block text-xs font-semibold mb-1">Fotoğraf URL</label><input value={form.guide?.image || ''} onChange={e => setGuide('image', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
              <div><label className="block text-xs font-semibold mb-1">Biyografi</label><textarea value={form.guide?.bio || ''} onChange={e => setGuide('bio', e.target.value)} rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] resize-none" /></div>
              <div><label className="block text-xs font-semibold mb-1">Instagram</label><input value={form.guide?.instagram || ''} onChange={e => setGuide('instagram', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" placeholder="@kullanici" /></div>
            </div>
          )}

          {/* SEO / AEO */}
          {tab === 'seo' && (
            <div className="space-y-4 max-w-2xl">
              <div>
                <label className="block text-xs font-semibold mb-1">Meta Title <span className="font-normal text-gray-400">({(form.metaTitle || '').length}/60)</span></label>
                <input value={form.metaTitle} onChange={e => set('metaTitle', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" maxLength={70} placeholder="Örn: Likya Yolu Fethiye Etabı — 4 Gece 5 Gün Rehberli Trekking" data-testid="tour-seo-metaTitle" />
                <p className="text-[11px] text-gray-400 mt-1">Boş bırakırsanız tur adı kullanılır. İdeal: 50-60 karakter.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Meta Description <span className="font-normal text-gray-400">({(form.metaDescription || '').length}/155)</span></label>
                <textarea value={form.metaDescription} onChange={e => set('metaDescription', e.target.value)} rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] resize-none" maxLength={170} placeholder="Ölüdeniz'den Patara'ya 4 gece 5 gün rehberli yürüyüş. Kokartlı rehber, konaklama, ulaşım ve öğünler dahil. 15 kişilik gruplar." data-testid="tour-seo-metaDescription" />
                <p className="text-[11px] text-gray-400 mt-1">İdeal: 140-155 karakter.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Odak Anahtar Kelime</label>
                <input value={form.focusKeyphrase} onChange={e => set('focusKeyphrase', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" placeholder="likya yolu fethiye turu" data-testid="tour-seo-focusKeyphrase" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Kapak Görsel Alt Metni</label>
                <input value={form.heroImageAlt} onChange={e => set('heroImageAlt', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" placeholder="Örn: Kelebekler Vadisi tepesinden Ölüdeniz manzarası" data-testid="tour-seo-heroImageAlt" />
                <p className="text-[11px] text-gray-400 mt-1">Google Görsel Arama + erişilebilirlik için.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-lg p-4 text-xs text-gray-600 leading-relaxed">
                <p className="font-semibold text-[#3D4A26] mb-2">AEO İpuçları</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>SSS sekmesinde en az 5-6 sık sorulan soru ekleyin (rota, ekipman, konaklama, iptal, seviye).</li>
                  <li>Meta description&apos;da tur adını + &quot;kaç gün&quot;, &quot;fiyat&quot;, &quot;dahil olanlar&quot; gibi arama niyetlerini geçirin.</li>
                  <li>Meta title&apos;da yıl belirtmeyin (yıla göre eskir); yerine &quot;rehberli&quot;, &quot;küçük grup&quot; gibi ayırt edici sözcükler.</li>
                </ul>
              </div>
            </div>
          )}

          {/* FAQ */}
          {tab === 'faq' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#1B1B1B]">Sıkça Sorulan Sorular</p>
                  <p className="text-xs text-gray-500">Turun altında hem kullanıcıya görünür hem de FAQPage JSON-LD olarak Google&apos;a sunulur.</p>
                </div>
                <button type="button" onClick={addFaq} className="flex items-center gap-1.5 bg-[#3D4A26] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#2d3a1a]" data-testid="tour-faq-add">
                  <Plus size={14} /> Soru Ekle
                </button>
              </div>

              {(form.faqItems || []).length === 0 && (
                <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500">Henüz soru eklenmedi</div>
              )}

              {(form.faqItems || []).map((f, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-2 bg-[#FAFAF7]">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-semibold text-[#3D4A26]">Soru #{i + 1}</span>
                    <button type="button" onClick={() => removeFaq(i)} className="text-red-500 hover:bg-red-50 rounded p-1"><Trash2 size={14} /></button>
                  </div>
                  <input value={f.question} onChange={e => updateFaq(i, 'question', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" placeholder="Örn: Bu tur için hangi kondisyon seviyesi gerekli?" data-testid={`tour-faq-question-${i}`} />
                  <textarea value={f.answer} onChange={e => updateFaq(i, 'answer', e.target.value)} rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] resize-none" placeholder="Cevap (40-70 kelime ideal)" data-testid={`tour-faq-answer-${i}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between mt-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} className="w-4 h-4 accent-[#C8D843]" />
            <span className="text-sm font-medium">Aktif (sitede görünsün)</span>
          </label>
          <button type="submit" disabled={saving}
            className="bg-[#3D4A26] text-white font-bold px-8 py-2.5 rounded-lg hover:bg-[#2d3a1a] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <><Loader2 size={16} className="animate-spin" /> Kaydediliyor...</> : (isEdit ? 'Güncelle' : 'Tur Oluştur')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminTourForm;
